import { Suspense, useState } from 'react'

import { useTranslation } from 'react-i18next'

import { confirmDialogGuard } from '@/components/alert-dialog/confirm-dialog-guard'
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui'
import { Skeleton } from '@/components/ui/skeleton'
import { DetalhesClienteAtendimentoChile } from '@/features/atendimento/chile/components/detalhes-cliente-atendimento-chile'
import { IdentificadorClienteAtendimento } from '@/features/atendimento/shared/components/identificador-cliente-atendimento'
import { useAtendimento } from '@/hooks/use-atendimento'
import { useGuardedHandler } from '@/hooks/use-guarded-handlers'
import { checkOnlineGuard } from '@/utils'

type Props = {
  nome: string
}

const DetalhesSkeletonChile = () => (
  <div className="w-80 space-y-4 px-3 py-2">
    <Skeleton className="h-5 w-40" />
    <div className="space-y-2">
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-full" />
    </div>
  </div>
)

export const AtendimentoDropdownMenuChile = ({ nome }: Props) => {
  const { t } = useTranslation('atendimento')
  const [detalhesAberto, setDetalhesAberto] = useState(false)
  const { handleEncerrarAtendimento } = useAtendimento()
  const handleEncerrarAtendimentoGuarded = useGuardedHandler(
    [
      checkOnlineGuard(),
      confirmDialogGuard({
        title: t('components.atendimento_dropdown.titulo'),
        description: t('components.atendimento_dropdown.descricao'),
      }),
    ],
    handleEncerrarAtendimento,
  )

  return (
    <div className="dropdown-container relative z-50">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="secondary" className="bg-foreground shadow-cz max-w-70 justify-start px-2 py-2.5">
            <IdentificadorClienteAtendimento nome={nome} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="flex w-70 flex-col gap-2">
          <DropdownMenuLabel className="text-base font-medium">
            {t('components.atendimento_info.o_que_fazer')}
          </DropdownMenuLabel>
          <DropdownMenuGroup className="font-nunito [&>div]:h-11">
            <DropdownMenuSub open={detalhesAberto} onOpenChange={setDetalhesAberto}>
              <DropdownMenuSubTrigger
                onClick={() => setDetalhesAberto(true)}
                onPointerMove={(e) => e.preventDefault()}
                onPointerLeave={(e) => e.preventDefault()}
              >
                {t('components.atendimento_info.ver_detalhes')}
              </DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent className="mr-2">
                  <Suspense fallback={<DetalhesSkeletonChile />}>
                    <DetalhesClienteAtendimentoChile />
                  </Suspense>
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub>
            <DropdownMenuItem onClick={handleEncerrarAtendimentoGuarded}>
              {t('components.atendimento_info.encerrar_atendimento')}
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
